import { importProvidersFrom, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';

import {
  ActionRequiredState,
  AppEnvironmentState,
  FeatureFlagConfigurationState,
  LIBRARY_CONFIG,
  OfflineHeaderModule,
  PermissionsState,
  SidebarV3Component,
  UserState,
} from '@gea/digital-ui-lib';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { environment } from '../environments/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxsModule } from '@ngxs/store';
import { SharedModule } from './pages/core/shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MsalInterceptorInterceptor } from './pages/core/shared/interceptors/msal-interceptor.interceptor';
import { LoadingInterceptor } from './pages/core/shared/interceptors/loading.interceptor';
import { GeaLibraryModule } from './pages/core/shared/gea-library.module';
import { PrimeNGModule } from './pages/core/shared/prime-ng.module';
import { providePrimeNG } from 'primeng/config';

const runtimeConfig = (window as Window & { __appConfig?: { primeNgLicense?: string } }).__appConfig;

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    NgxsModule.forRoot([UserState, ActionRequiredState, AppEnvironmentState, PermissionsState, FeatureFlagConfigurationState], {
      compatibility: {
        strictContentSecurityPolicy: true,
      },
    }),
    BrowserModule,
    AppRoutingModule,
    MsalModule.forRoot(new PublicClientApplication({
      auth: {
        clientId: environment.msalConfig.auth.clientId,
        authority: environment.msalConfig.auth.authority,
        redirectUri: environment.msalConfig.auth.redirectUri,
        postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
        navigateToLoginRequestUrl: true
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: true, // set to true for IE 11
      },
      system: {
        loggerOptions: {
          loggerCallback: () => { },
          piiLoggingEnabled: false
        }
      }
    }), {
      interactionType: InteractionType.Redirect, // MSAL Guard Configuration
      authRequest: {
        scopes: environment.apiScopes
      },
      loginFailedRoute: "/login-failed"
    }, {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map([
        ['https://graph.microsoft.com/v1.0/me', ['user.Read']],
        [environment.apiUrl, environment.apiScopes],
        [environment.ioTestApiUrl, environment.apiScopes],
        [environment.apiUrlCodexAIDiagramGenius, environment.apiScopes],
      ]),
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    SharedModule,
    BrowserAnimationsModule,
    OfflineHeaderModule,
    SidebarV3Component,
    GeaLibraryModule,
    PrimeNGModule
  ],
  providers: [importProvidersFrom(HttpClientModule),
  ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json', enforceLoading: true }),
  {
    provide: LIBRARY_CONFIG,
    useFactory: () => {
      return {

        baseUrl: "http://localhost:4200/",
        legalDocumentsStorageURL: "http://localhost:4200/",
        iconStorageURL: "assets/icons/"
      };
    },
  },
  { provide: 'portalLink', useValue: environment.msalConfig.auth.redirectUri },
  { provide: 'redirectLink', useValue: environment.msalConfig.auth.redirectUri },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptorInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: LoadingInterceptor,
    multi: true
  },
  providePrimeNG({
    license: runtimeConfig?.primeNgLicense ?? ''
  })
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
